import { RegistrationSteps } from '../../registration-steps.js';
import { engineFieldAttributes } from '../../registration-form/client/fields/engine-field-attributes.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { PublicFooter } from '../../../client/public-footer.js';
import { GroupsAPI } from '../../../groups/api/GroupsAPI.js';
import type { Person } from '../../../users/entities/person.js';
import { inActiveUI, isSusEntryFormActive } from '../../isSusEntryFormActive.js';
import { SgSusEntryFormSchoolRegistrationDialog } from './sg-sus-entry-form-school-registration.js';

export const SgSusEntryFormSchoolSelection = stateful(() => {
  const [attempt, setAttempt] = useState(0);
  const [search, setSearch] = useState('');
  const [step, setStep] = useState<1 | 2>(1);
  const openRegistration = async (...args: Parameters<typeof SgSusEntryFormSchoolRegistrationDialog>) => {
    setStep(2);
    try {
      await SgSusEntryFormSchoolRegistrationDialog(...args);
    } finally {
      setStep(1);
    }
  };
  const {
    value: schools,
    loading,
    error,
  } = useQuery(
    () =>
      GroupsAPI.getPublicSchools()({
        _id: 1,
        name: 1,
        bundesland: 1,
        mode: 1,
        imageID: 1,
        image: selectionSet(() => ImageFile, 2),
        schoolType: 1,
      }),
    [attempt],
  );
  const {
    value: isActive,
    loading: activeLoading,
    error: activeError,
  } = useQuery(() => isSusEntryFormActive()(), [attempt]);
  const user = useObservable(() => currentUser<Person>(), []);

  useEffect(() => {
    if (!user || !schools?.length || !isActive) return;
    const params = new URLSearchParams(location.search);
    const school = schools.find(s => s._id === params.get('schoolID'));
    if (!school) return;
    params.delete('schoolID');
    history.replaceState(null, '', `${location.pathname}${params.size ? `?${params}` : ''}`);
    void openRegistration(school, true);
  }, [schools, user, isActive]);

  const findSchools = (query: string) =>
    (schools ?? []).filter(s =>
      `${s.name} ${s.schoolType ?? ''}`.toLocaleLowerCase('de').includes(query.trim().toLocaleLowerCase('de')),
    );
  const visibleSchools = findSchools(search);
  return html` <div class="flex min-h-0 min-w-0 flex-1 flex-col">
    <div class="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
      <header
        class="relative isolate shrink-0 overflow-hidden rounded-b-xl bg-linear-to-r from-primary to-secondary text-white"
      >
        <div class="absolute inset-0 -z-10 bg-black/40 sm:bg-black/20" aria-hidden="true"></div>
        <div class="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-8 text-center sm:px-6 sm:py-10 lg:py-12">
          <h1 class="w-full text-2xl font-bold leading-tight text-balance sm:text-3xl lg:text-4xl">
            Willkommen bei der Bewerbung an den Schulen der Schulgesellschaft
          </h1>
          <p class="w-full text-base leading-relaxed text-pretty sm:text-lg">
            Bitte wählen Sie die Schule aus, an der sie eine Bewerbung für ihr Kind abgeben möchten.
          </p>
        </div>
      </header>
      <main class="mx-auto flex w-full max-w-7xl flex-1 flex-col gap-4 px-4 py-6 sm:px-6 sm:py-8">
        ${RegistrationSteps(step)}
        ${
          loading || activeLoading
            ? html`<div role="status" class="flex items-center gap-3 py-8">
                <span class="loading loading-spinner" aria-hidden="true"></span>Schulen werden geladen …
              </div>`
            : error || activeError
              ? html`<div role="alert" class="alert alert-error alert-soft text-base-content">
                  <div>
                    <h2 class="font-semibold">Schulen konnten nicht geladen werden</h2>
                    <p>Bitte versuchen Sie es erneut.</p>
                  </div>
                  <button type="button" class="btn" @click=${() => setAttempt(attempt + 1)}>Erneut versuchen</button>
                </div>`
              : !isActive
                ? inActiveUI
                : !schools?.length
                  ? html`<section class="card">
                      <div class="card-body gap-3">
                        <h2 class="font-semibold md:text-lg">Derzeit keine Schule zur Auswahl</h2>
                        <p>
                          Für die Online-Bewerbung ist aktuell keine Schule freigegeben. Bitte wenden Sie sich an das
                          Sekretariat Ihrer gewünschten Schule oder versuchen Sie es später erneut.
                        </p>
                        <button
                          type="button"
                          class="btn btn-outline self-start"
                          @click=${() => setAttempt(attempt + 1)}
                        >
                          Auswahl aktualisieren
                        </button>
                      </div>
                    </section>`
                  : html`
                      <div
                        class="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-end gap-3 sm:grid-cols-2 lg:grid-cols-3"
                      >
                        <div
                          class="flex min-w-0 w-full flex-col gap-2 [&_input]:text-base [&_input::-webkit-search-cancel-button]:appearance-none [&_[class*='keyboard-arrow-down']]:hidden"
                          ${engineFieldAttributes({ id: 'sus-school-search', label: 'Schule suchen', type: 'search' })}
                          @input=${(event: Event) => {
                            if (event.target instanceof HTMLInputElement) setSearch(event.target.value);
                          }}
                        >
                          <label
                            class="flex items-center gap-2 text-sm font-semibold text-base-content/70"
                            for="sus-school-search"
                          >
                            <span
                              class="icon-[material-symbols--school-outline-rounded] text-xl text-primary"
                              aria-hidden="true"
                            ></span>
                            Schule suchen
                          </label>
                          ${DropdownSelectionInput<string>({
                            ariaLabel: 'Schule suchen',
                            placeholder: 'Name oder Schulart',
                            value: search,
                            search: query => findSchools(query).flatMap(school => (school.name ? [school.name] : [])),
                            onChanged: value => setSearch(value ?? ''),
                            searchable: true,
                            freeText: true,
                            clearable: true,
                            debounceMs: 100,
                            emptyState: 'Keine passende Schule gefunden.',
                            suffix: html`<span
                              class="icon-[material-symbols--search-rounded] text-xl"
                              aria-hidden="true"
                            ></span>`,
                          })}
                        </div>
                        <p
                          role="status"
                          class="flex h-11 items-center justify-self-end whitespace-nowrap text-sm text-base-content/70 sm:col-start-2 sm:h-auto lg:col-start-3"
                        >
                          ${visibleSchools.length} ${visibleSchools.length === 1 ? 'Schule' : 'Schulen'}
                        </p>
                      </div>
                      <div class="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        ${visibleSchools.map(
                          school =>
                            html` <button
                              type="button"
                              class="card min-w-0 text-left transition-colors hover:border-primary focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
                              @click=${() => openRegistration(school, !!user)}
                            >
                              <span
                                class="card-body min-h-20 w-full flex-row items-center gap-3 p-4 sm:min-h-24 sm:p-5"
                              >
                                ${school.image ? html`<img class="h-10 w-14 shrink-0 object-contain" src=${school.image.getServeLink()} alt="" loading="lazy" />` : nothing}
                                <span class="min-w-0 flex-1 text-base font-semibold break-words sm:text-lg"
                                  >${school.name}</span
                                >
                                <span
                                  class="icon-[material-symbols--arrow-forward-rounded] shrink-0 text-xl text-primary"
                                  aria-hidden="true"
                                ></span>
                              </span>
                            </button>`,
                        )}
                      </div>
                      ${visibleSchools.length ? nothing : html`<p>Keine passende Schule gefunden. Versuchen Sie einen anderen Suchbegriff.</p>`}
                    `
        }
      </main>
    </div>
    ${PublicFooter({ compactMobile: true })}
  </div>`;
});
